import moment from "moment";
import queryString from "query-string";
import { useHistory, useLocation } from "react-router-dom";

/**
 * Hook para gerenciar query params na URL
 * Usa query-string com arrayFormat: 'comma' para serializar arrays
 * Suporta serialização de objetos Moment para ISO strings
 * 
 * @returns {Object} Objeto com métodos para manipular query params
 */
const useQueryParams = () => {
  const history = useHistory();
  const location = useLocation();

  /**
   * Lê todos os query params da URL atual
   * @returns {Object} Objeto com os query params parseados
   */
  const getQueryParams = () => {
    return queryString.parse(location.search, {
      arrayFormat: "comma",
      parseNumbers: true,
      parseBooleans: true,
    });
  };

  /**
   * Lê um query param específico da URL
   * @param {string} key - Nome do parâmetro
   * @returns {any} Valor do parâmetro ou undefined
   */
  const getQueryParam = (key) => {
    const params = getQueryParams();
    return params[key];
  };

  /**
   * Atualiza os query params na URL
   * Remove parâmetros com valores vazios (null, undefined, '')
   * @param {Object} params - Objeto com os novos parâmetros
   * @param {boolean} replace - Se true, substitui a entrada no histórico ao invés de adicionar
   */
  const setQueryParams = (params, replace = false) => {
    // Remove valores vazios
    const cleanedParams = removeEmptyParams(params);
    
    // Serializa os parâmetros
    const searchString = queryString.stringify(cleanedParams, {
      arrayFormat: "comma",
      skipNull: true,
      skipEmptyString: true,
    });

    // Atualiza a URL
    const newUrl = searchString ? `${location.pathname}?${searchString}` : location.pathname;
    
    if (replace) {
      history.replace(newUrl);
    } else {
      history.push(newUrl);
    }

    return newUrl;
  };

  /**
   * Remove um ou mais query params da URL
   * @param {string|string[]} keys - Nome(s) do(s) parâmetro(s) a remover
   */
  const removeQueryParams = (keys) => {
    const currentParams = getQueryParams();
    const keysArray = Array.isArray(keys) ? keys : [keys];
    
    keysArray.forEach((key) => {
      delete currentParams[key];
    });
    
    setQueryParams(currentParams, true);
  };

  /**
   * Limpa todos os query params da URL
   */
  const clearQueryParams = () => {
    history.push(location.pathname);
  };

  /**
   * Remove parâmetros com valores vazios de um objeto
   * @param {Object} params - Objeto com parâmetros
   * @returns {Object} Objeto sem valores vazios
   */
  const removeEmptyParams = (params) => {
    const cleaned = {};
    
    Object.keys(params).forEach((key) => {
      const value = params[key];
      
      // Ignora valores null, undefined e strings vazias
      if (value !== null && value !== undefined && value !== "") {
        // Para arrays, verifica se tem elementos
        if (Array.isArray(value)) {
          if (value.length > 0) {
            cleaned[key] = value;
          }
        } else if (moment.isMoment(value)) {
          // Serializa objetos Moment para ISO string
          cleaned[key] = value.toISOString();
        } else {
          cleaned[key] = value;
        }
      }
    });
    
    return cleaned;
  };

  /**
   * Serializa um valor para query param
   * @param {any} value - Valor a serializar
   * @returns {string} Valor serializado
   */
  const serializeValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(",");
    }
    return String(value);
  };

  /**
   * Desserializa um valor de query param
   * @param {string} value - Valor a desserializar
   * @param {string} type - Tipo esperado ('array', 'number', 'boolean', 'string')
   * @returns {any} Valor desserializado
   */
  const deserializeValue = (value, type = "string") => {
    if (value === undefined || value === null) {
      return value;
    }

    switch (type) {
      case "array":
        return Array.isArray(value) ? value : [value];
      case "number":
        return Number(value);
      case "boolean":
        return value === "true" || value === true;
      default:
        return value;
    }
  };

  return {
    getQueryParams,
    getQueryParam,
    setQueryParams,
    removeQueryParams,
    clearQueryParams,
    removeEmptyParams,
    serializeValue,
    deserializeValue,
  };
};

export default useQueryParams;
